/** What a must-gate entry's `match` can hold for one payload field: a scalar the call must equal, or a list its list must start with. */
export type MustGateMatchValue = string | number | boolean

/** A must-gate entry's `match`: the payload fields, in the shape a gate row's `match` takes. */
export type MustGateMatch = Record<string, MustGateMatchValue | MustGateMatchValue[]>

/**
 * One entry of a tool binding's must-gate list (#949). A tool name makes every call to that tool must-gate. An object
 * narrows it to the calls whose payload carries `match`, as a gate row's `match` narrows a gate: a code host whose one
 * pull-request tool both opens and merges can make only the merge must-gate (`{ method: "merge" }`).
 */
export type MustGateEntry = string | { tool: string; match: MustGateMatch }

/** The tool a must-gate entry names. */
export function mustGateEntryTool(entry: MustGateEntry): string {
    return typeof entry === 'string' ? entry : entry.tool
}

/** The calls an entry narrows to, or null when it holds every call of its tool. */
export function mustGateEntryMatch(entry: MustGateEntry): MustGateMatch | null {
    return typeof entry === 'string' ? null : entry.match
}

/** An entry as a person reads it: the tool, then what its calls carry, such as `vendor.tool (method=merge)`. */
export function mustGateEntryLabel(entry: MustGateEntry): string {
    const match = mustGateEntryMatch(entry)
    if (!match) {
        return mustGateEntryTool(entry)
    }
    const fields = Object.entries(match).map(
        ([key, value]) => `${key}=${Array.isArray(value) ? value.join(' ') : String(value)}`,
    )
    return `${mustGateEntryTool(entry)} (${fields.join(', ')})`
}
